<template>
  <div class="signup-page">
    <div class="signup-container">
      <div class="signup-header">
        <h1 class="welcome-title">Join the DietEdics Community!</h1>
        <p class="welcome-subtitle">
          Connect with fellow dietitians, discover amazing continuing education opportunities, 
          and take your career to the next level.
        </p>
      </div>

      <div class="signup-card">
        <!-- Account Type Selection -->
        <div v-if="!accountType" class="account-type-selection">
          <h2 class="selection-title">Choose Your Account Type</h2>
          <div class="account-type-buttons">
            <button 
              type="button" 
              @click="selectAccountType('dietitian')" 
              class="account-type-button"
            >
              <div class="button-icon">👩‍⚕️</div>
              <h3>Dietitian Account</h3>
              <p>For individual dietitians looking to discover and review continuing education courses</p>
            </button>
            <button 
              type="button" 
              @click="selectAccountType('provider')" 
              class="account-type-button"
            >
              <div class="button-icon">🏢</div>
              <h3>Provider Account</h3>
              <p>For organizations offering continuing education courses</p>
            </button>
          </div>
        </div>

        <!-- Signup Form -->
        <div v-else>
          <div class="form-header">
            <button 
              type="button" 
              @click="goBack" 
              class="back-button"
              aria-label="Go back to account type selection"
            >
              ← Back
            </button>
            <h2 class="form-title">
              {{ accountType === 'provider' ? 'Provider Account Signup' : 'Dietitian Account Signup' }}
            </h2>
          </div>

          <div v-if="error" class="error-message">
            <strong>Oops!</strong> {{ error }}
          </div>

          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Creating your account...</p>
          </div>

          <form v-else @submit.prevent="handleSignup" class="signup-form">
            <!-- Provider Search Section (only for provider accounts) -->
            <div v-if="accountType === 'provider'" class="form-section provider-search-section">
              <h2 class="section-title">Claim Your Provider</h2>
              
              <!-- Selected Provider Display -->
              <div v-if="selectedProvider" class="selected-provider-display">
                <div class="selected-provider-card">
                  <div class="selected-provider-header">
                    <h3 class="selected-provider-title">Selected Provider</h3>
                    <button 
                      type="button" 
                      @click="removeSelectedProvider" 
                      class="remove-provider-button"
                      aria-label="Remove selected provider"
                    >
                      Change Provider
                    </button>
                  </div>
                  <div class="selected-provider-content">
                    <h4 class="provider-name">{{ selectedProvider.name }}</h4>
                    <p class="provider-type">{{ selectedProvider.provider_type }}</p>
                    <p v-if="selectedProvider.city && selectedProvider.state" class="provider-location">
                      📍 {{ selectedProvider.city }}, {{ selectedProvider.state }}
                    </p>
                    <p v-if="selectedProvider.website" class="provider-website">
                      <a :href="selectedProvider.website" target="_blank" rel="noopener noreferrer">
                        {{ selectedProvider.website }}
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <!-- Provider Search (shown when no provider is selected) -->
              <div v-else>
                <p class="section-description">
                  Search for your organization to claim it. If your organization isn't listed, you can claim it after signup.
                </p>
                
                <div class="form-group">
                  <label for="providerSearch" class="form-label">
                    Search for Provider
                  </label>
                  <div class="search-container">
                    <input
                      type="text"
                      id="providerSearch"
                      v-model="providerSearchTerm"
                      @input="searchProviders"
                      placeholder="Type to search for your organization..."
                      autocomplete="off"
                      class="form-input search-input"
                    />
                    <div v-if="searchingProviders" class="search-loading">Searching...</div>
                  </div>
                  
                  <!-- Provider Search Results -->
                  <div v-if="providerSearchResults.length > 0" class="provider-results">
                    <div 
                      v-for="provider in providerSearchResults" 
                      :key="provider.id"
                      @click="selectProvider(provider)"
                      class="provider-result-item"
                    >
                      <div class="provider-info">
                        <h4 class="provider-name">{{ provider.name }}</h4>
                        <p class="provider-type">{{ provider.provider_type }}</p>
                        <p v-if="provider.city && provider.state" class="provider-location">
                          {{ provider.city }}, {{ provider.state }}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div v-if="providerSearchTerm && !searchingProviders && providerSearchResults.length === 0" class="no-results">
                    No providers found. You can claim your organization after signup.
                  </div>
                </div>
              </div>
            </div>

            <!-- Common Form Fields -->
            <div class="form-section">
              <h2 class="section-title">Your Information</h2>
              
              <div class="form-row">
                <div class="form-group">
                  <label for="firstName" class="form-label">
                    First Name <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    v-model="formData.firstName"
                    required
                    placeholder="Jane"
                    autocomplete="given-name"
                    class="form-input"
                    :class="{ 'error': errors.firstName }"
                  />
                  <span v-if="errors.firstName" class="field-error">{{ errors.firstName }}</span>
                </div>

                <div class="form-group">
                  <label for="lastName" class="form-label">
                    Last Name <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    v-model="formData.lastName"
                    required
                    placeholder="Smith"
                    autocomplete="family-name"
                    class="form-input"
                    :class="{ 'error': errors.lastName }"
                  />
                  <span v-if="errors.lastName" class="field-error">{{ errors.lastName }}</span>
                </div>
              </div>

              <div class="form-group">
                <label for="email" class="form-label">
                  Email Address <span class="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  v-model="formData.email"
                  required
                  placeholder="jane.smith@example.com"
                  autocomplete="email"
                  class="form-input"
                  :class="{ 'error': errors.email }"
                />
                <span v-if="errors.email" class="field-error">{{ errors.email }}</span>
                <p class="field-hint">We'll never share your email with anyone</p>
              </div>

              <div class="form-group">
                <label for="password" class="form-label">
                  Password <span class="required">*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  v-model="formData.password"
                  required
                  minlength="6"
                  placeholder="Create a secure password"
                  autocomplete="new-password"
                  class="form-input"
                  :class="{ 'error': errors.password }"
                />
                <span v-if="errors.password" class="field-error">{{ errors.password }}</span>
                <p class="field-hint">At least 6 characters</p>
              </div>

              <div class="form-group">
                <label for="passwordConfirmation" class="form-label">
                  Confirm Password <span class="required">*</span>
                </label>
                <input
                  type="password"
                  id="passwordConfirmation"
                  v-model="formData.passwordConfirmation"
                  required
                  placeholder="Confirm your password"
                  autocomplete="new-password"
                  class="form-input"
                  :class="{ 'error': errors.passwordConfirmation }"
                />
                <span v-if="errors.passwordConfirmation" class="field-error">{{ errors.passwordConfirmation }}</span>
              </div>
            </div>

            <!-- Professional Details (only for dietitian accounts) -->
            <div v-if="accountType === 'dietitian'" class="form-section">
              <h2 class="section-title">Professional Details</h2>
              
              <div class="form-row">
                <div class="form-group">
                  <label for="city" class="form-label">
                    City <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="city"
                    v-model="formData.city"
                    required
                    placeholder="New York"
                    autocomplete="address-level2"
                    class="form-input"
                    :class="{ 'error': errors.city }"
                  />
                  <span v-if="errors.city" class="field-error">{{ errors.city }}</span>
                </div>

                <div class="form-group">
                  <label for="state" class="form-label">
                    State <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="state"
                    v-model="formData.state"
                    required
                    placeholder="NY"
                    maxlength="2"
                    autocomplete="address-level1"
                    class="form-input"
                    :class="{ 'error': errors.state }"
                  />
                  <span v-if="errors.state" class="field-error">{{ errors.state }}</span>
                </div>
              </div>

              <div class="form-group">
                <label for="experienceLevel" class="form-label">
                  Experience Level <span class="required">*</span>
                </label>
                <select
                  id="experienceLevel"
                  v-model="formData.experienceLevel"
                  required
                  autocomplete="off"
                  class="form-input"
                  :class="{ 'error': errors.experienceLevel }"
                >
                  <option value="">Select your experience level</option>
                  <option value="Student">Student</option>
                  <option value="New Graduate (0-2 years)">New Graduate (0-2 years)</option>
                  <option value="Early Career (3-5 years)">Early Career (3-5 years)</option>
                  <option value="Mid Career (6-10 years)">Mid Career (6-10 years)</option>
                  <option value="Experienced (11-15 years)">Experienced (11-15 years)</option>
                  <option value="Senior (16+ years)">Senior (16+ years)</option>
                </select>
                <span v-if="errors.experienceLevel" class="field-error">{{ errors.experienceLevel }}</span>
              </div>

              <div class="form-group">
                <label for="professionalRole" class="form-label">
                  Professional Role
                </label>
                <select
                  id="professionalRole"
                  v-model="formData.professionalRole"
                  autocomplete="off"
                  class="form-input"
                  :class="{ 'error': errors.professionalRole }"
                >
                  <option value="">Select your professional role (optional)</option>
                  <option value="rdn">RDN (Registered Dietitian Nutritionist)</option>
                  <option value="ndtr">NDTR (Nutrition and Dietetics Technician, Registered)</option>
                </select>
                <span v-if="errors.professionalRole" class="field-error">{{ errors.professionalRole }}</span>
                <p class="field-hint">This helps us customize your experience</p>
              </div>
            </div>

            <!-- Optional Information (only for dietitian accounts) -->
            <div v-if="accountType === 'dietitian'" class="form-section optional-section">
              <h2 class="section-title">Optional Information</h2>
              <p class="section-description">
                Help other dietitians find you and verify your credentials
              </p>
              
              <div class="form-group">
                <label for="licenseNumber" class="form-label">
                  License Number
                </label>
                <input
                  type="text"
                  id="licenseNumber"
                  v-model="formData.licenseNumber"
                  placeholder="RD-12345"
                  autocomplete="off"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <label for="academyNumber" class="form-label">
                  Academy of Nutrition and Dietetics Number
                </label>
                <input
                  type="text"
                  id="academyNumber"
                  v-model="formData.academyNumber"
                  placeholder="12345678"
                  autocomplete="off"
                  class="form-input"
                />
              </div>
            </div>

            <div class="form-actions">
              <button type="submit" class="submit-button" :disabled="loading">
                <span v-if="!loading">Create My Account</span>
                <span v-else>Creating Account...</span>
              </button>
              <p class="signin-link">
                Already have an account? 
                <button type="button" @click="goToSignIn" class="link-button">Sign In</button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { signup } from '../../services/authService.js'
import { searchProviders } from '../../services/providerService.js'

export default {
  name: 'SignUpPage',
  data() {
    return {
      accountType: null, // 'dietitian' or 'provider'
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        passwordConfirmation: '',
        city: '',
        state: '',
        experienceLevel: '',
        professionalRole: '',
        licenseNumber: '',
        academyNumber: ''
      },
      providerSearchTerm: '',
      providerSearchResults: [],
      selectedProvider: null,
      searchingProviders: false,
      searchTimeout: null,
      errors: {},
      error: null,
      loading: false
    }
  },
  methods: {
    selectAccountType(type) {
      this.accountType = type
      // Reset form when switching types
      this.resetForm()
    },
    goBack() {
      this.accountType = null
      this.resetForm()
    },
    resetForm() {
      this.formData = {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        passwordConfirmation: '',
        city: '',
        state: '',
        experienceLevel: '',
        professionalRole: '',
        licenseNumber: '',
        academyNumber: ''
      }
      this.providerSearchTerm = ''
      this.providerSearchResults = []
      this.selectedProvider = null
      this.errors = {}
      this.error = null
    },
    async searchProviders() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // If search term is empty, clear results
      if (!this.providerSearchTerm || this.providerSearchTerm.trim().length === 0) {
        this.providerSearchResults = []
        return
      }

      // Debounce search
      this.searchTimeout = setTimeout(async () => {
        this.searchingProviders = true
        try {
          const results = await searchProviders(this.providerSearchTerm.trim())
          this.providerSearchResults = results
        } catch (error) {
          console.error('Error searching providers:', error)
          this.providerSearchResults = []
        } finally {
          this.searchingProviders = false
        }
      }, 300)
    },
    selectProvider(provider) {
      this.selectedProvider = provider
      // Clear search results and search term after selection
      this.providerSearchResults = []
      this.providerSearchTerm = ''
    },
    removeSelectedProvider() {
      this.selectedProvider = null
      // Clear search term when removing selection
      this.providerSearchTerm = ''
      this.providerSearchResults = []
    },
    async handleSignup() {
      // Clear previous errors
      this.errors = {}
      this.error = null

      // Validate passwords match
      if (this.formData.password !== this.formData.passwordConfirmation) {
        this.errors.passwordConfirmation = 'Passwords do not match'
        return
      }

      // Validate state is 2 characters (for dietitian accounts)
      if (this.accountType === 'dietitian' && this.formData.state && this.formData.state.length !== 2) {
        this.errors.state = 'Please enter a 2-letter state code'
        return
      }

      // Validate required fields for dietitian accounts
      if (this.accountType === 'dietitian') {
        if (!this.formData.city) {
          this.errors.city = 'City is required'
          return
        }
        if (!this.formData.state) {
          this.errors.state = 'State is required'
          return
        }
        if (!this.formData.experienceLevel) {
          this.errors.experienceLevel = 'Experience level is required'
          return
        }
      }

      this.loading = true

      try {
        const signupData = {
          firstName: this.formData.firstName,
          lastName: this.formData.lastName,
          email: this.formData.email,
          password: this.formData.password,
          city: this.formData.city || '',
          state: this.formData.state ? this.formData.state.toUpperCase() : '',
          experienceLevel: this.formData.experienceLevel || '',
          professionalRole: this.formData.professionalRole || null,
          licenseNumber: this.formData.licenseNumber || null,
          academyNumber: this.formData.academyNumber || null
        }

        // Add provider_id if provider account and provider is selected
        if (this.accountType === 'provider' && this.selectedProvider) {
          signupData.providerId = this.selectedProvider.id
        }

        const user = await signup(signupData)

        // Emit success event
        this.$emit('signup-success', user)
        
        // Navigate to landing page
        this.$router.push({ name: 'landing' })
      } catch (error) {
        console.error('Signup error:', error)
        
        // Handle validation errors from API
        if (error.data && error.data.status) {
          this.error = error.data.status.message || 'Something went wrong. Please try again.'
        } else if (error.data && error.data.errors) {
          const apiErrors = error.data.errors
          if (Array.isArray(apiErrors)) {
            this.error = apiErrors.join(', ')
          } else if (typeof apiErrors === 'object') {
            // Map Rails error format to our form fields
            Object.keys(apiErrors).forEach(key => {
              const camelKey = this.toCamelCase(key)
              if (this.formData[camelKey] !== undefined) {
                this.errors[camelKey] = Array.isArray(apiErrors[key]) 
                  ? apiErrors[key][0] 
                  : apiErrors[key]
              }
            })
            if (Object.keys(this.errors).length === 0) {
              this.error = 'Please check your information and try again'
            }
          } else {
            this.error = apiErrors
          }
        } else {
          this.error = error.message || 'Something went wrong. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },
    goToSignIn() {
      this.$router.push({ name: 'landing' })
      this.$emit('show-login')
    },
    toCamelCase(str) {
      return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase())
    }
  }
}
</script>

<style scoped>
.signup-page {
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-secondary-light) 100%);
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.signup-container {
  max-width: 800px;
  width: 100%;
}

.signup-header {
  text-align: center;
  margin-bottom: 40px;
  color: var(--color-text-inverse);
}

.welcome-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.welcome-subtitle {
  font-size: 1.2rem;
  line-height: 1.6;
  opacity: 0.95;
  max-width: 600px;
  margin: 0 auto;
}

.signup-card {
  background: var(--color-surface-light);
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

/* Account Type Selection */
.account-type-selection {
  text-align: center;
}

.selection-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 32px;
}

.account-type-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 32px;
}

.account-type-button {
  background: var(--color-surface-light);
  border: 3px solid var(--color-border-main);
  border-radius: 12px;
  padding: 32px 24px;
  cursor: pointer;
  transition: all var(--transition-normal);
  text-align: center;
}

.account-type-button:hover {
  border-color: var(--color-primary-main);
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.account-type-button h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--color-primary-main);
  margin: 16px 0 12px;
}

.account-type-button p {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
}

.button-icon {
  font-size: 3rem;
  margin-bottom: 8px;
}

/* Form Header */
.form-header {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-button {
  background: none;
  border: none;
  color: var(--color-primary-main);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  transition: color var(--transition-normal);
}

.back-button:hover {
  color: var(--color-primary-dark);
}

.form-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.error-message {
  background: var(--color-error-light);
  color: var(--color-error-dark);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border-left: 4px solid var(--color-error-main);
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--color-primary-light);
  border-top-color: var(--color-primary-main);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.signup-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-primary-main);
  margin-bottom: 8px;
  border-bottom: 2px solid var(--color-primary-light);
  padding-bottom: 8px;
}

.section-description {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  margin-top: -12px;
  margin-bottom: 8px;
}

.optional-section .section-title {
  color: var(--color-text-secondary);
  border-bottom-color: var(--color-border-light);
}

/* Provider Search */
.provider-search-section {
  background: var(--color-bg-main);
  padding: 24px;
  border-radius: 12px;
  border: 2px solid var(--color-border-light);
}

/* Selected Provider Display */
.selected-provider-display {
  margin-top: 8px;
}

.selected-provider-card {
  background: var(--color-surface-light);
  border: 2px solid var(--color-primary-main);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.selected-provider-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--color-primary-light);
}

.selected-provider-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-primary-main);
  margin: 0;
}

.remove-provider-button {
  background: var(--color-surface-light);
  border: 2px solid var(--color-primary-main);
  color: var(--color-primary-main);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.remove-provider-button:hover {
  background: var(--color-primary-main);
  color: var(--color-text-inverse);
}

.selected-provider-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selected-provider-content .provider-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.selected-provider-content .provider-type {
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.selected-provider-content .provider-location {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.selected-provider-content .provider-website {
  margin: 8px 0 0 0;
}

.selected-provider-content .provider-website a {
  color: var(--color-primary-main);
  text-decoration: none;
  font-size: 0.9rem;
  word-break: break-all;
}

.selected-provider-content .provider-website a:hover {
  text-decoration: underline;
}

.search-container {
  position: relative;
}

.search-input {
  width: 100%;
}

.search-loading {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.provider-results {
  margin-top: 16px;
  max-height: 300px;
  overflow-y: auto;
  border: 2px solid var(--color-border-main);
  border-radius: 8px;
  background: var(--color-surface-light);
}

.provider-result-item {
  padding: 16px;
  border-bottom: 1px solid var(--color-border-light);
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.provider-result-item:last-child {
  border-bottom: none;
}

.provider-result-item:hover {
  background: var(--color-bg-main);
}

.provider-result-item.selected {
  background: var(--color-primary-light);
  border-left: 4px solid var(--color-primary-main);
}

.provider-info {
  flex: 1;
}

.provider-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 4px 0;
}

.provider-type {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin: 0 0 4px 0;
}

.provider-location {
  font-size: 0.85rem;
  color: var(--color-text-light);
  margin: 0;
}

.selected-indicator {
  color: var(--color-primary-main);
  font-weight: 600;
  font-size: 0.9rem;
}

.no-results {
  margin-top: 12px;
  padding: 12px;
  background: var(--color-info-light);
  color: var(--color-text-secondary);
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: center;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.95rem;
}

.required {
  color: var(--color-error-main);
}

.form-input {
  padding: 12px 16px;
  border: 2px solid var(--color-border-main);
  border-radius: 8px;
  font-size: 1rem;
  transition: all var(--transition-normal);
  background: var(--color-surface-light);
  color: var(--color-text-primary);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
  box-shadow: 0 0 0 3px var(--color-primary-light);
}

.form-input.error {
  border-color: var(--color-error-main);
}

.form-input.error:focus {
  box-shadow: 0 0 0 3px var(--color-error-light);
}

.field-error {
  color: var(--color-error-main);
  font-size: 0.85rem;
  font-weight: 500;
}

.field-hint {
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  margin-top: -4px;
}

.form-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
}

.submit-button {
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--color-primary-main) 0%, var(--color-primary-dark) 100%);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.submit-button:active:not(:disabled) {
  transform: translateY(0);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.signin-link {
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
}

.link-button {
  background: none;
  border: none;
  color: var(--color-primary-main);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: inherit;
}

.link-button:hover {
  color: var(--color-primary-dark);
}

@media (max-width: 768px) {
  .signup-page {
    padding: 20px 16px;
  }

  .signup-card {
    padding: 24px;
  }

  .welcome-title {
    font-size: 2rem;
  }

  .welcome-subtitle {
    font-size: 1rem;
  }

  .account-type-buttons {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 1.3rem;
  }
}
</style>
